import React, { useEffect, useState, useRef } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import { Users, Building2, FileText, Plus, Edit, Trash2, X, Eye, Printer, Mail, Save, Languages, Loader2, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { generateContractHTML } from '../utils/contractTemplate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminContractsPage = () => {
  const { getAuthHeaders, language } = useApp();
  const [activeTab, setActiveTab] = useState('tenants');
  const [importingPdf, setImportingPdf] = useState(false);
  const pdfInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [tenants, setTenants] = useState([]);
  const [owners, setOwners] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [template, setTemplate] = useState({ content_ro: '', content_en: '', content_fr: '' });
  
  // Dialog states
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [showOwnerDialog, setShowOwnerDialog] = useState(false);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editingOwner, setEditingOwner] = useState(null);
  const [editingContract, setEditingContract] = useState(null);
  const [previewContract, setPreviewContract] = useState(null);
  const [translatingTemplate, setTranslatingTemplate] = useState(false);

  // Form data
  const [tenantForm, setTenantForm] = useState({
    full_name: '', country: '', address: '', passport_id: '', passport_valid_until: '',
    phone: '', phone_prefix: '+40', whatsapp: '', email: ''
  });
  
  const [ownerForm, setOwnerForm] = useState({
    full_name: '', address: '', id_series: '', id_number: '', id_issued_by: '',
    phone: '', email: '', bank_name: '', bank_country: 'Romania', iban: '', swift_bic: ''
  });
  
  const [contractForm, setContractForm] = useState({
    tenant_id: '', owner_id: '', apartment_id: '', language: 'ro_en',
    start_date: '', end_date: '', duration_months: 12,
    rent_eur: '', deposit_eur: '', first_payment_date: '', first_payment_amount: '',
    second_payment_date: '', second_payment_amount: '', monthly_payment_day: 1,
    keys_handover_date: '', status: 'draft', signed_date: '', notes: ''
  });

  // Fetch all data
  useEffect(() => {
    fetchTenants();
    fetchOwners();
    fetchContracts();
    fetchApartments();
    fetchTemplate();
  }, []);

  const fetchTenants = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contracts/tenants`, { headers: getAuthHeaders() });
      setTenants(data);
    } catch (error) {
      console.error('Failed to fetch tenants');
    }
  };

  const fetchOwners = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contracts/owners`, { headers: getAuthHeaders() });
      setOwners(data);
    } catch (error) {
      console.error('Failed to fetch owners');
    }
  };

  const fetchContracts = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contracts`, { headers: getAuthHeaders() });
      setContracts(data);
    } catch (error) {
      console.error('Failed to fetch contracts');
    }
  };

  const fetchApartments = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/apartments`);
      setApartments(data);
    } catch (error) {
      console.error('Failed to fetch apartments');
    }
  };

  const fetchTemplate = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/contracts/template`, { headers: getAuthHeaders() });
      if (data.content_ro || data.content_en) {
        setTemplate(data);
      } else {
        // Set default template
        setTemplate({
          content_ro: CONTRACT_TEMPLATE.ro,
          content_en: CONTRACT_TEMPLATE.en,
          content_fr: ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch template');
    }
  };

  // Tenant CRUD
  const handleSaveTenant = async () => {
    setLoading(true);
    try {
      if (editingTenant) {
        await axios.put(`${BACKEND_URL}/api/contracts/tenants/${editingTenant.id}`, tenantForm, { headers: getAuthHeaders() });
        toast.success('Client actualizat!');
      } else {
        await axios.post(`${BACKEND_URL}/api/contracts/tenants`, tenantForm, { headers: getAuthHeaders() });
        toast.success('Client adăugat!');
      }
      fetchTenants();
      setShowTenantDialog(false);
      resetTenantForm();
    } catch (error) {
      toast.error('Eroare la salvare');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTenant = async (id) => {
    if (!window.confirm('Ștergi acest client?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contracts/tenants/${id}`, { headers: getAuthHeaders() });
      toast.success('Client șters!');
      fetchTenants();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const resetTenantForm = () => {
    setEditingTenant(null);
    setTenantForm({
      full_name: '', country: '', address: '', passport_id: '', passport_valid_until: '',
      phone: '', phone_prefix: '+40', whatsapp: '', email: ''
    });
  };

  // Owner CRUD
  const handleSaveOwner = async () => {
    setLoading(true);
    try {
      if (editingOwner) {
        await axios.put(`${BACKEND_URL}/api/contracts/owners/${editingOwner.id}`, ownerForm, { headers: getAuthHeaders() });
        toast.success('Proprietar actualizat!');
      } else {
        await axios.post(`${BACKEND_URL}/api/contracts/owners`, ownerForm, { headers: getAuthHeaders() });
        toast.success('Proprietar adăugat!');
      }
      fetchOwners();
      setShowOwnerDialog(false);
      resetOwnerForm();
    } catch (error) {
      toast.error('Eroare la salvare');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOwner = async (id) => {
    if (!window.confirm('Ștergi acest proprietar?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contracts/owners/${id}`, { headers: getAuthHeaders() });
      toast.success('Proprietar șters!');
      fetchOwners();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const resetOwnerForm = () => {
    setEditingOwner(null);
    setOwnerForm({
      full_name: '', address: '', id_series: '', id_number: '', id_issued_by: '',
      phone: '', email: '', bank_name: '', bank_country: 'Romania', iban: '', swift_bic: ''
    });
  };

  // Contract CRUD
  const handleSaveContract = async () => {
    setLoading(true);
    try {
      // Convert empty strings to proper values for numeric fields
      const contractData = {
        ...contractForm,
        rent_eur: contractForm.rent_eur === '' ? 0 : parseFloat(contractForm.rent_eur) || 0,
        deposit_eur: contractForm.deposit_eur === '' ? 0 : parseFloat(contractForm.deposit_eur) || 0,
        first_payment_amount: contractForm.first_payment_amount === '' ? 0 : parseFloat(contractForm.first_payment_amount) || 0,
        second_payment_amount: contractForm.second_payment_amount === '' ? 0 : parseFloat(contractForm.second_payment_amount) || 0,
        duration_months: contractForm.duration_months === '' ? 12 : parseInt(contractForm.duration_months) || 12,
        monthly_payment_day: contractForm.monthly_payment_day === '' ? 1 : parseInt(contractForm.monthly_payment_day) || 1,
      };
      
      if (editingContract) {
        await axios.put(`${BACKEND_URL}/api/contracts/${editingContract.id}`, contractData, { headers: getAuthHeaders() });
        toast.success('Contract actualizat!');
      } else {
        await axios.post(`${BACKEND_URL}/api/contracts`, contractData, { headers: getAuthHeaders() });
        toast.success('Contract creat!');
      }
      fetchContracts();
      setShowContractDialog(false);
      resetContractForm();
    } catch (error) {
      console.error('Contract save error:', error.response?.data || error);
      toast.error('Eroare la salvare: ' + (error.response?.data?.detail || 'Verifică datele'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContract = async (id) => {
    if (!window.confirm('Ștergi acest contract?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/contracts/${id}`, { headers: getAuthHeaders() });
      toast.success('Contract șters!');
      fetchContracts();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  const resetContractForm = () => {
    setEditingContract(null);
    setContractForm({
      tenant_id: '', owner_id: '', apartment_id: '', language: 'ro_en',
      start_date: '', end_date: '', duration_months: 12,
      rent_eur: '', deposit_eur: '', first_payment_date: '', first_payment_amount: '',
      second_payment_date: '', second_payment_amount: '', monthly_payment_day: 1,
      keys_handover_date: '', status: 'draft', signed_date: '', notes: ''
    });
  };

  // Template save
  const handleSaveTemplate = async () => {
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/contracts/template`, template, { headers: getAuthHeaders() });
      toast.success('Template salvat!');
    } catch (error) {
      toast.error('Eroare la salvare template');
    } finally {
      setLoading(false);
    }
  };

  // Translate EN to FR
  const handleTranslateToFrench = async () => {
    if (!template.content_en) {
      toast.error('Completează mai întâi partea în engleză');
      return;
    }
    setTranslatingTemplate(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/translate`, {
        text: template.content_en,
        source_lang: 'en',
        target_langs: ['fr']
      }, { headers: getAuthHeaders() });
      setTemplate({ ...template, content_fr: data.fr });
      toast.success('Traducere completă!');
    } catch (error) {
      toast.error('Eroare la traducere');
    } finally {
      setTranslatingTemplate(false);
    }
  };

  // Import PDF and extract text
  const handleImportPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Selectează un fișier PDF');
      return;
    }

    setImportingPdf(true);
    toast.info('Se procesează PDF-ul... Aceasta poate dura câteva secunde.');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const { data } = await axios.post(`${BACKEND_URL}/api/contracts/import-pdf`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (data.text_ro) {
        setTemplate(prev => ({
          ...prev,
          content_ro: data.text_ro,
          content_en: data.text_en || prev.content_en
        }));
        toast.success('PDF importat cu succes! Verifică și editează textul.');
      } else {
        toast.error('Nu s-a putut extrage textul din PDF');
      }
    } catch (error) {
      console.error('PDF import error:', error);
      toast.error('Eroare la importul PDF-ului');
    } finally {
      setImportingPdf(false);
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
    }
  };

  // Prepare data and generate contract HTML using imported template
  const prepareContractData = (contract) => {
    const tenant = tenants.find(t => t.id === contract.tenant_id);
    const owner = owners.find(o => o.id === contract.owner_id);
    const apartment = apartments.find(a => a.id === contract.apartment_id);
    
    return {
      contractDate: contract.signed_date || new Date().toLocaleDateString('ro-RO'),
      ownerName: owner?.full_name || '_____________________',
      ownerAddress: owner?.address || '_____________________',
      ownerIdSeries: owner?.id_series || '___',
      ownerIdNumber: owner?.id_number || '_______',
      ownerIdIssuedBy: owner?.id_issued_by || '_____________',
      ownerBank: owner?.bank_name || '_____________',
      ownerBankCountry: owner?.bank_country || 'Romania',
      ownerIban: owner?.iban || 'RO__________________________',
      ownerSwift: owner?.swift_bic || '_________',
      tenantName: tenant?.full_name || '_____________________',
      tenantCountry: tenant?.country || '_____________',
      tenantAddress: tenant?.address || '_____________________',
      tenantPassport: tenant?.passport_id || '_____________',
      tenantPassportValid: tenant?.passport_valid_until || '_____________',
      tenantPhone: `${tenant?.phone_prefix || ''} ${tenant?.phone || '_____________'}`,
      tenantWhatsapp: tenant?.whatsapp || '_____________',
      tenantEmail: tenant?.email || '_____________________',
      apartmentAddress: apartment?.real_address || apartment?.title?.ro || '_____________________',
      durationMonths: contract.duration_months || '___',
      startDate: contract.start_date || '_____________',
      endDate: contract.end_date || '_____________',
      rentEur: contract.rent_eur || '___',
      depositEur: contract.deposit_eur || '___',
      firstPaymentDate: contract.first_payment_date || '_____________',
      firstPaymentAmount: contract.first_payment_amount || '___',
      keysDate: contract.keys_handover_date || '_____________',
      monthlyPaymentDay: contract.monthly_payment_day || '1',
    };
  };

  // Generate contract preview (simple text for dialog)
  const generateContractPreview = (contract) => {
    const tenant = tenants.find(t => t.id === contract.tenant_id);
    const owner = owners.find(o => o.id === contract.owner_id);
    const apartment = apartments.find(a => a.id === contract.apartment_id);
    
    return `CONTRACT DE ÎNCHIRIERE / RENTING AGREEMENT
din/from ${contract.signed_date || new Date().toLocaleDateString('ro-RO')}

═══════════════════════════════════════════════════════════════

ART. 1 PĂRȚILE / PARTIES

PROPRIETAR / OWNER:
${owner?.full_name || '_______________'}
Adresă: ${owner?.address || '_______________'}
CI: seria ${owner?.id_series || '___'} nr. ${owner?.id_number || '_______'}

CHIRIAȘ / TENANT:
${tenant?.full_name || '_______________'}
Țara: ${tenant?.country || '_______________'}
Adresă: ${tenant?.address || '_______________'}
Pașaport/CI: ${tenant?.passport_id || '_______________'}
Telefon: ${tenant?.phone_prefix || ''} ${tenant?.phone || '_______________'}
Email: ${tenant?.email || '_______________'}

═══════════════════════════════════════════════════════════════

ART. 2 OBIECTUL CONTRACTULUI
Apartament: ${apartment?.real_address || apartment?.title?.ro || '_______________'}

ART. 3 DURATA CONTRACTULUI
Perioada: ${contract.duration_months || '___'} luni
De la: ${contract.start_date || '_______________'}
Până la: ${contract.end_date || '_______________'}

ART. 4 CHIRIA
Chirie lunară: ${contract.rent_eur || '___'} EUR
Garanție: ${contract.deposit_eur || '___'} EUR
IBAN: ${owner?.iban || '_______________'}
Bancă: ${owner?.bank_name || '_______________'}

═══════════════════════════════════════════════════════════════

Data semnării: ${contract.signed_date || '_______________'}

PROPRIETAR                           CHIRIAȘ
${owner?.full_name || '_______________'}                    ${tenant?.full_name || '_______________'}

_________________                    _________________
    Semnătură                            Signature`;
  };

  // Handle email contract
  const handleEmailContract = async (contract) => {
    const tenant = tenants.find(t => t.id === contract.tenant_id);
    if (!tenant?.email) {
      toast.error('Clientul nu are adresă de email setată!');
      return;
    }
    
    const emailTo = window.prompt(`Trimite contractul pe email la:`, tenant.email);
    if (!emailTo) return;
    
    toast.info(`Se trimite contractul către ${emailTo}...`);
    
    try {
      const formData = new FormData();
      formData.append('to_email', emailTo);
      
      await axios.post(`${BACKEND_URL}/api/contracts/${contract.id}/send-email`, formData, {
        headers: getAuthHeaders()
      });
      
      toast.success(`Contract trimis cu succes către ${emailTo}!`);
    } catch (error) {
      console.error('Email send error:', error);
      const errorMsg = error.response?.data?.detail || 'Eroare la trimitere email';
      toast.error(errorMsg);
    }
  };

  // Handle print from list
  const handlePrintContractFromList = (contract) => {
    const data = prepareContractData(contract);
    const html = generateContractHTML(data);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const handlePreviewContract = (contract) => {
    const preview = generateContractPreview(contract);
    const data = prepareContractData(contract);
    setPreviewContract({ ...contract, preview, html: generateContractHTML(data) });
    setShowPreviewDialog(true);
  };

  // Old handlePrintContract removed - now using inline function in Preview Dialog

  const tabs = [
    { id: 'tenants', label: 'Clienți', icon: Users },
    { id: 'owners', label: 'Proprietari', icon: Building2 },
    { id: 'template', label: 'Tipar Contract', icon: FileText },
    { id: 'active', label: 'Contracte Active', icon: FileText },
    { id: 'create', label: 'Creare Contract', icon: Plus },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Gestionare Contracte</h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 text-sm rounded-t transition-colors ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/5 text-muted-foreground hover:bg-white/10'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="glass p-6">
          {/* TENANTS TAB */}
          {activeTab === 'tenants' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold">Clienți (Chiriași)</h2>
                <Button onClick={() => { resetTenantForm(); setShowTenantDialog(true); }} className="bg-primary">
                  <Plus size={16} className="mr-2" /> Adaugă Client
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3">Nume</th>
                      <th className="text-left p-3">Țară</th>
                      <th className="text-left p-3">Telefon</th>
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Pașaport/CI</th>
                      <th className="text-right p-3">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((tenant) => (
                      <tr key={tenant.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 font-medium">{tenant.full_name}</td>
                        <td className="p-3">{tenant.country}</td>
                        <td className="p-3">{tenant.phone_prefix} {tenant.phone}</td>
                        <td className="p-3">{tenant.email}</td>
                        <td className="p-3">{tenant.passport_id}</td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => {
                              setEditingTenant(tenant);
                              setTenantForm(tenant);
                              setShowTenantDialog(true);
                            }}
                            className="p-2 hover:bg-white/10 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteTenant(tenant.id)}
                            className="p-2 hover:bg-red-500/20 text-red-400 rounded ml-2"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {tenants.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nu există clienți</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* OWNERS TAB */}
          {activeTab === 'owners' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold">Proprietari</h2>
                <Button onClick={() => { resetOwnerForm(); setShowOwnerDialog(true); }} className="bg-primary">
                  <Plus size={16} className="mr-2" /> Adaugă Proprietar
                </Button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3">Nume</th>
                      <th className="text-left p-3">Adresă</th>
                      <th className="text-left p-3">CI</th>
                      <th className="text-left p-3">Telefon</th>
                      <th className="text-left p-3">IBAN</th>
                      <th className="text-right p-3">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {owners.map((owner) => (
                      <tr key={owner.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="p-3 font-medium">{owner.full_name}</td>
                        <td className="p-3 max-w-xs truncate">{owner.address}</td>
                        <td className="p-3">{owner.id_series} {owner.id_number}</td>
                        <td className="p-3">{owner.phone}</td>
                        <td className="p-3 font-mono text-xs">{owner.iban}</td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => {
                              setEditingOwner(owner);
                              setOwnerForm(owner);
                              setShowOwnerDialog(true);
                            }}
                            className="p-2 hover:bg-white/10 rounded"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteOwner(owner.id)}
                            className="p-2 hover:bg-red-500/20 text-red-400 rounded ml-2"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {owners.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Nu există proprietari</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TEMPLATE TAB */}
          {activeTab === 'template' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">Tipar Contract</h2>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={pdfInputRef}
                    onChange={handleImportPdf}
                    accept=".pdf"
                    className="hidden"
                  />
                  <Button 
                    onClick={() => pdfInputRef.current?.click()} 
                    disabled={importingPdf} 
                    variant="outline"
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                  >
                    {importingPdf ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                    Import PDF
                  </Button>
                  <Button onClick={handleTranslateToFrench} disabled={translatingTemplate} variant="outline">
                    {translatingTemplate ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Languages size={16} className="mr-2" />}
                    Traduce EN → FR
                  </Button>
                  <Button onClick={handleSaveTemplate} disabled={loading} className="bg-primary">
                    <Save size={16} className="mr-2" /> Salvează Template
                  </Button>
                </div>
              </div>
              
              <div className="bg-amber-900/20 border border-amber-500/30 rounded p-3 text-sm">
                <p className="text-amber-300">
                  <strong>💡 Sfat:</strong> Poți importa un PDF contract existent folosind butonul "Import PDF". 
                  Textul va fi extras automat și poți edita în câmpurile de mai jos.
                </p>
              </div>
              
              <p className="text-sm text-muted-foreground">
                Folosește placeholder-uri: __OWNER_NAME__, __TENANT_NAME__, __APARTMENT_ADDRESS__, __RENT_EUR__, __DEPOSIT_EUR__, etc.
              </p>
              
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">🇷🇴 Română (Introducere)</label>
                  <textarea
                    value={template.content_ro}
                    onChange={(e) => setTemplate({ ...template, content_ro: e.target.value })}
                    rows={12}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">🇬🇧 Engleză (Articole)</label>
                  <textarea
                    value={template.content_en}
                    onChange={(e) => setTemplate({ ...template, content_en: e.target.value })}
                    rows={20}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3 text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">🇫🇷 Franceză (Auto-tradus sau manual)</label>
                  <textarea
                    value={template.content_fr}
                    onChange={(e) => setTemplate({ ...template, content_fr: e.target.value })}
                    rows={20}
                    placeholder="Apasă 'Traduce EN → FR' pentru a traduce automat sau completează manual..."
                    className="w-full bg-slate-800 border border-white/10 rounded p-3 text-sm font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ACTIVE CONTRACTS TAB */}
          {activeTab === 'active' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold">Contracte Active</h2>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-3">Nr. Contract</th>
                      <th className="text-left p-3">Client</th>
                      <th className="text-left p-3">Apartament</th>
                      <th className="text-left p-3">Perioadă</th>
                      <th className="text-left p-3">Chirie</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-right p-3">Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contracts.map((contract) => {
                      const tenant = tenants.find(t => t.id === contract.tenant_id);
                      const apartment = apartments.find(a => a.id === contract.apartment_id);
                      return (
                        <tr key={contract.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="p-3 font-mono">{contract.contract_number}</td>
                          <td className="p-3">{tenant?.full_name || '-'}</td>
                          <td className="p-3">{apartment?.title?.[language] || apartment?.title?.ro || '-'}</td>
                          <td className="p-3">{contract.start_date} - {contract.end_date}</td>
                          <td className="p-3">{contract.rent_eur} EUR</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              contract.status === 'active' ? 'bg-green-500/20 text-green-400' :
                              contract.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {contract.status}
                            </span>
                          </td>
                          <td className="p-3 text-right flex justify-end gap-1">
                            <button onClick={() => handlePreviewContract(contract)} className="p-2 hover:bg-white/10 rounded" title="Preview">
                              <Eye size={16} />
                            </button>
                            <button onClick={() => handlePrintContractFromList(contract)} className="p-2 hover:bg-blue-500/20 text-blue-400 rounded" title="Tipărește">
                              <Printer size={16} />
                            </button>
                            <button onClick={() => handleEmailContract(contract)} className="p-2 hover:bg-green-500/20 text-green-400 rounded" title="Trimite Email">
                              <Mail size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setEditingContract(contract);
                                setContractForm(contract);
                                setShowContractDialog(true);
                              }}
                              className="p-2 hover:bg-white/10 rounded"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button onClick={() => handleDeleteContract(contract.id)} className="p-2 hover:bg-red-500/20 text-red-400 rounded" title="Delete">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {contracts.length === 0 && (
                      <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Nu există contracte</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CREATE CONTRACT TAB */}
          {activeTab === 'create' && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Creare Contract Nou</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Client (Chiriaș) *</label>
                  <select
                    value={contractForm.tenant_id}
                    onChange={(e) => setContractForm({ ...contractForm, tenant_id: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  >
                    <option value="">-- Selectează Client --</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Proprietar *</label>
                  <select
                    value={contractForm.owner_id}
                    onChange={(e) => setContractForm({ ...contractForm, owner_id: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  >
                    <option value="">-- Selectează Proprietar --</option>
                    {owners.map((o) => <option key={o.id} value={o.id}>{o.full_name}</option>)}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Apartament *</label>
                  <select
                    value={contractForm.apartment_id}
                    onChange={(e) => setContractForm({ ...contractForm, apartment_id: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  >
                    <option value="">-- Selectează Apartament --</option>
                    {apartments.map((a) => <option key={a.id} value={a.id}>{a.title?.[language] || a.title?.ro}</option>)}
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Limba Contract</label>
                  <select
                    value={contractForm.language}
                    onChange={(e) => setContractForm({ ...contractForm, language: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  >
                    <option value="ro_en">🇷🇴 Română + 🇬🇧 Engleză</option>
                    <option value="ro_fr">🇷🇴 Română + 🇫🇷 Franceză</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Data Început *</label>
                  <input
                    type="date"
                    value={contractForm.start_date}
                    onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Durată (luni)</label>
                  <input
                    type="number"
                    value={contractForm.duration_months}
                    onChange={(e) => setContractForm({ ...contractForm, duration_months: parseInt(e.target.value) || 12 })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Data Sfârșit *</label>
                  <input
                    type="date"
                    value={contractForm.end_date}
                    onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Chirie (EUR/lună) *</label>
                  <input
                    type="number"
                    value={contractForm.rent_eur}
                    onChange={(e) => setContractForm({ ...contractForm, rent_eur: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Garanție (EUR) *</label>
                  <input
                    type="number"
                    value={contractForm.deposit_eur}
                    onChange={(e) => setContractForm({ ...contractForm, deposit_eur: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Data Primei Plăți</label>
                  <input
                    type="date"
                    value={contractForm.first_payment_date}
                    onChange={(e) => setContractForm({ ...contractForm, first_payment_date: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Suma Prima Plată (EUR)</label>
                  <input
                    type="number"
                    value={contractForm.first_payment_amount}
                    onChange={(e) => setContractForm({ ...contractForm, first_payment_amount: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Data Predare Chei</label>
                  <input
                    type="date"
                    value={contractForm.keys_handover_date}
                    onChange={(e) => setContractForm({ ...contractForm, keys_handover_date: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Zi Plată Lunară</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={contractForm.monthly_payment_day}
                    onChange={(e) => setContractForm({ ...contractForm, monthly_payment_day: parseInt(e.target.value) || 1 })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Data Semnării</label>
                  <input
                    type="date"
                    value={contractForm.signed_date}
                    onChange={(e) => setContractForm({ ...contractForm, signed_date: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Status</label>
                  <select
                    value={contractForm.status}
                    onChange={(e) => setContractForm({ ...contractForm, status: e.target.value })}
                    className="w-full bg-slate-800 border border-white/10 rounded p-3"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Activ</option>
                    <option value="completed">Finalizat</option>
                    <option value="terminated">Reziliat</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Note</label>
                <textarea
                  value={contractForm.notes}
                  onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-800 border border-white/10 rounded p-3"
                />
              </div>
              
              <div className="flex gap-4">
                <Button onClick={handleSaveContract} disabled={loading || !contractForm.tenant_id || !contractForm.owner_id || !contractForm.apartment_id} className="bg-primary">
                  <Save size={16} className="mr-2" /> Salvează Contract
                </Button>
                <Button
                  onClick={() => handlePreviewContract(contractForm)}
                  disabled={!contractForm.tenant_id || !contractForm.owner_id || !contractForm.apartment_id}
                  variant="outline"
                >
                  <Eye size={16} className="mr-2" /> Preview
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tenant Dialog */}
      <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
        <DialogContent className="bg-slate-950 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTenant ? 'Editare Client' : 'Adaugă Client Nou'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2">
              <label className="text-sm mb-1 block">Nume Complet *</label>
              <input
                type="text"
                value={tenantForm.full_name}
                onChange={(e) => setTenantForm({ ...tenantForm, full_name: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Țară</label>
              <input
                type="text"
                value={tenantForm.country}
                onChange={(e) => setTenantForm({ ...tenantForm, country: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm mb-1 block">Adresă</label>
              <input
                type="text"
                value={tenantForm.address}
                onChange={(e) => setTenantForm({ ...tenantForm, address: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Nr. Pașaport / CI</label>
              <input
                type="text"
                value={tenantForm.passport_id}
                onChange={(e) => setTenantForm({ ...tenantForm, passport_id: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Valabil până la</label>
              <input
                type="date"
                value={tenantForm.passport_valid_until}
                onChange={(e) => setTenantForm({ ...tenantForm, passport_valid_until: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Prefix Telefon</label>
              <select
                value={tenantForm.phone_prefix}
                onChange={(e) => setTenantForm({ ...tenantForm, phone_prefix: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              >
                <option value="+40">+40 (Romania)</option>
                <option value="+33">+33 (France)</option>
                <option value="+44">+44 (UK)</option>
                <option value="+49">+49 (Germany)</option>
                <option value="+39">+39 (Italy)</option>
                <option value="+34">+34 (Spain)</option>
                <option value="+1">+1 (USA/Canada)</option>
                <option value="+373">+373 (Moldova)</option>
                <option value="+380">+380 (Ukraine)</option>
              </select>
            </div>
            <div>
              <label className="text-sm mb-1 block">Telefon</label>
              <input
                type="tel"
                value={tenantForm.phone}
                onChange={(e) => setTenantForm({ ...tenantForm, phone: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">WhatsApp</label>
              <input
                type="tel"
                value={tenantForm.whatsapp}
                onChange={(e) => setTenantForm({ ...tenantForm, whatsapp: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Email</label>
              <input
                type="email"
                value={tenantForm.email}
                onChange={(e) => setTenantForm({ ...tenantForm, email: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowTenantDialog(false)}>Anulează</Button>
            <Button onClick={handleSaveTenant} disabled={loading || !tenantForm.full_name} className="bg-primary">
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
              Salvează
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Owner Dialog */}
      <Dialog open={showOwnerDialog} onOpenChange={setShowOwnerDialog}>
        <DialogContent className="bg-slate-950 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingOwner ? 'Editare Proprietar' : 'Adaugă Proprietar Nou'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2">
              <label className="text-sm mb-1 block">Nume Complet *</label>
              <input
                type="text"
                value={ownerForm.full_name}
                onChange={(e) => setOwnerForm({ ...ownerForm, full_name: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm mb-1 block">Adresă Completă</label>
              <input
                type="text"
                value={ownerForm.address}
                onChange={(e) => setOwnerForm({ ...ownerForm, address: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
                placeholder="Mun. Iași, Jud. Iași, Str. ..., nr. ..., bl. ..., sc. ..., et. ..., ap. ..."
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">CI Seria</label>
              <input
                type="text"
                value={ownerForm.id_series}
                onChange={(e) => setOwnerForm({ ...ownerForm, id_series: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
                placeholder="MX"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">CI Nr.</label>
              <input
                type="text"
                value={ownerForm.id_number}
                onChange={(e) => setOwnerForm({ ...ownerForm, id_number: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div className="col-span-2">
              <label className="text-sm mb-1 block">CI Emis de</label>
              <input
                type="text"
                value={ownerForm.id_issued_by}
                onChange={(e) => setOwnerForm({ ...ownerForm, id_issued_by: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
                placeholder="SPCLEP Iași"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Telefon</label>
              <input
                type="tel"
                value={ownerForm.phone}
                onChange={(e) => setOwnerForm({ ...ownerForm, phone: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Email</label>
              <input
                type="email"
                value={ownerForm.email}
                onChange={(e) => setOwnerForm({ ...ownerForm, email: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div className="col-span-2 border-t border-white/10 pt-4 mt-2">
              <h3 className="font-medium mb-3">Detalii Bancare</h3>
            </div>
            <div>
              <label className="text-sm mb-1 block">Bancă</label>
              <input
                type="text"
                value={ownerForm.bank_name}
                onChange={(e) => setOwnerForm({ ...ownerForm, bank_name: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">Țara Bancă</label>
              <input
                type="text"
                value={ownerForm.bank_country}
                onChange={(e) => setOwnerForm({ ...ownerForm, bank_country: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2"
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">IBAN</label>
              <input
                type="text"
                value={ownerForm.iban}
                onChange={(e) => setOwnerForm({ ...ownerForm, iban: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2 font-mono"
                placeholder="RO..."
              />
            </div>
            <div>
              <label className="text-sm mb-1 block">SWIFT/BIC</label>
              <input
                type="text"
                value={ownerForm.swift_bic}
                onChange={(e) => setOwnerForm({ ...ownerForm, swift_bic: e.target.value })}
                className="w-full bg-slate-800 border border-white/10 rounded p-2 font-mono"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowOwnerDialog(false)}>Anulează</Button>
            <Button onClick={handleSaveOwner} disabled={loading || !ownerForm.full_name} className="bg-primary">
              {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
              Salvează
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Edit Dialog */}
      <Dialog open={showContractDialog} onOpenChange={setShowContractDialog}>
        <DialogContent className="bg-slate-950 border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editare Contract {editingContract?.contract_number}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 mt-4">
            {/* Same fields as Create tab but in dialog */}
            <div>
              <label className="text-sm mb-1 block">Client</label>
              <select value={contractForm.tenant_id} onChange={(e) => setContractForm({ ...contractForm, tenant_id: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2">
                <option value="">--</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm mb-1 block">Proprietar</label>
              <select value={contractForm.owner_id} onChange={(e) => setContractForm({ ...contractForm, owner_id: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2">
                <option value="">--</option>
                {owners.map((o) => <option key={o.id} value={o.id}>{o.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm mb-1 block">Apartament</label>
              <select value={contractForm.apartment_id} onChange={(e) => setContractForm({ ...contractForm, apartment_id: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2">
                <option value="">--</option>
                {apartments.map((a) => <option key={a.id} value={a.id}>{a.title?.[language] || a.title?.ro}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm mb-1 block">Început</label>
              <input type="date" value={contractForm.start_date} onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2" />
            </div>
            <div>
              <label className="text-sm mb-1 block">Sfârșit</label>
              <input type="date" value={contractForm.end_date} onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2" />
            </div>
            <div>
              <label className="text-sm mb-1 block">Status</label>
              <select value={contractForm.status} onChange={(e) => setContractForm({ ...contractForm, status: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2">
                <option value="draft">Draft</option>
                <option value="active">Activ</option>
                <option value="completed">Finalizat</option>
                <option value="terminated">Reziliat</option>
              </select>
            </div>
            <div>
              <label className="text-sm mb-1 block">Chirie EUR</label>
              <input type="number" value={contractForm.rent_eur} onChange={(e) => setContractForm({ ...contractForm, rent_eur: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2" />
            </div>
            <div>
              <label className="text-sm mb-1 block">Garanție EUR</label>
              <input type="number" value={contractForm.deposit_eur} onChange={(e) => setContractForm({ ...contractForm, deposit_eur: e.target.value })} className="w-full bg-slate-800 border border-white/10 rounded p-2" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowContractDialog(false)}>Anulează</Button>
            <Button onClick={handleSaveContract} disabled={loading} className="bg-primary">
              <Save size={16} className="mr-2" /> Salvează
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="bg-slate-950 border-white/10 max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview Contract - {previewContract?.contract_number || 'Nou'}</DialogTitle>
          </DialogHeader>
          <div className="bg-white rounded mt-4 overflow-hidden" style={{ maxHeight: '60vh', overflow: 'auto' }}>
            {previewContract?.html ? (
              <iframe 
                srcDoc={previewContract.html}
                style={{ width: '100%', height: '500px', border: 'none' }}
                title="Contract Preview"
              />
            ) : (
              <pre className="whitespace-pre-wrap font-serif text-black p-8 text-sm">{previewContract?.preview}</pre>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>Închide</Button>
            <Button 
              onClick={() => {
                if (previewContract?.html) {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(previewContract.html);
                  printWindow.document.close();
                  setTimeout(() => printWindow.print(), 500);
                }
              }} 
              className="bg-blue-600"
            >
              <Printer size={16} className="mr-2" /> Tipărește PDF
            </Button>
            <Button 
              onClick={() => {
                if (previewContract) handleEmailContract(previewContract);
              }}
              className="bg-green-600"
            >
              <Mail size={16} className="mr-2" /> Trimite Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminContractsPage;
